'use client';

import { useMemo, useState } from 'react';
import {
  calculateAge,
  parseDateInput,
  todayInputValue,
  type AgeBreakdown,
} from '@/lib/browser-gencalc-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

interface AgeState {
  result: AgeBreakdown | null;
  error: string | null;
}

export default function BrowserAgeCalculator() {
  const [birthDate, setBirthDate] = useState('');
  const [asOfDate, setAsOfDate] = useState(todayInputValue());
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const { result, error }: AgeState = useMemo(() => {
    if (birthDate.trim().length === 0) {
      return { result: null, error: null };
    }
    const birth = parseDateInput(birthDate);
    if (!birth) {
      return { result: null, error: 'Enter a valid birth date.' };
    }
    const asOf = asOfDate.trim().length === 0 ? new Date() : parseDateInput(asOfDate);
    if (!asOf) {
      return { result: null, error: 'Enter a valid "as of" date.' };
    }
    const computed = calculateAge(birth, asOf);
    if (!computed) {
      return { result: null, error: 'The "as of" date must be on or after the birth date.' };
    }
    return { result: computed, error: null };
  }, [birthDate, asOfDate]);

  const summary = useMemo(() => {
    if (!result) {
      return '';
    }
    return [
      `Age: ${result.years} years, ${result.months} months, ${result.days} days`,
      `Total months: ${result.totalMonths.toLocaleString()}`,
      `Total weeks: ${result.totalWeeks.toLocaleString()}`,
      `Total days: ${result.totalDays.toLocaleString()}`,
      `Total hours: ${result.totalHours.toLocaleString()}`,
      `Born on a: ${result.weekdayBorn}`,
      `Next birthday: ${result.nextBirthdayDate} (${result.nextBirthdayInDays} days away)`,
    ].join('\n');
  }, [result]);

  async function handleCopy() {
    try {
      await copyText(summary);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  const statItems = result
    ? [
        { label: 'Years', value: result.years.toLocaleString() },
        { label: 'Months', value: result.months.toLocaleString() },
        { label: 'Days', value: result.days.toLocaleString() },
        { label: 'Total months', value: result.totalMonths.toLocaleString() },
        { label: 'Total weeks', value: result.totalWeeks.toLocaleString() },
        { label: 'Total days', value: result.totalDays.toLocaleString() },
      ]
    : [];

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Age</span>
        <h2 className="tool-live-title">Age calculator</h2>
        <p className="tool-live-subtitle">
          Enter a birth date to find an exact age in years, months, and days, plus totals and a
          countdown to the next birthday. All math runs in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Birth date</span>
              <input
                max={todayInputValue()}
                onChange={(event) => setBirthDate(event.target.value)}
                type="date"
                value={birthDate}
              />
            </label>
            <label className="tool-live-field">
              <span>As of date</span>
              <input
                onChange={(event) => setAsOfDate(event.target.value)}
                type="date"
                value={asOfDate}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={!result}
              onClick={handleCopy}
              type="button"
            >
              Copy summary
            </button>
          </div>
          {error ? <p className="status-message status-error">{error}</p> : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          {result ? (
            <>
              <p
                style={{
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  margin: '0 0 0.75rem',
                }}
              >
                {result.years} years, {result.months} months, {result.days} days
              </p>
              <div
                style={{
                  display: 'grid',
                  gap: '0.5rem',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  marginBottom: '0.75rem',
                }}
              >
                {statItems.map((item) => (
                  <div
                    key={item.label}
                    style={{
                      border: '1px solid rgba(148, 163, 184, 0.35)',
                      borderRadius: '0.5rem',
                      padding: '0.5rem 0.65rem',
                    }}
                  >
                    <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>{item.label}</div>
                    <div style={{ fontSize: '1rem', fontWeight: 600 }}>{item.value}</div>
                  </div>
                ))}
              </div>
              <p className="tool-live-panel-note">
                Born on a {result.weekdayBorn}. Next birthday: {result.nextBirthdayDate} (
                {result.nextBirthdayInDays} days away). Calculated locally in your browser.
              </p>
            </>
          ) : (
            <p className="tool-live-panel-note">
              Enter a birth date to see the full age breakdown. Nothing is uploaded.
            </p>
          )}
        </section>
      </div>
    </div>
  );
}
